import { PayData, PaymentHandler, PayResult, StoreLocator } from '@/store/models';
import loadScript from '@/commons/scriptLoad';
import checkout from '@/store/modules/checkout';
import storelocator from '@/store/modules/storelocator';
import { i18n } from '@/i18n';
import { getStreetNumber } from '@/commons/addressNormalizer';
import user from '@/store/modules/user';
import store from '@/store';
import { get as dataGetter } from 'lodash';
import { klarnaWaitAuthCallback, klarnaUpdateSession } from '@/store/api';
import { redirectToLocation } from '@/commons/redirectManager';

const sdkUrl = 'https://x.klarnacdn.net/kp/lib/v1/api.js';
const methodMap: any = {
    klarna_pay_later: 'pay_later',
};

export default class KlarnaHandler implements PaymentHandler {
    private availableMethods?: any;

    public init(params: any) {
        this.availableMethods = params.klarna_kp.available_methods;
        window.klarnaAsyncCallback = () => {
            Klarna.Payments.init({client_token: params.klarna_kp.client_token});
            // if (this.availableMethods && this.availableMethods.length === 1) {
            //     this.loadWidget(methodMap[this.availableMethods[0].type]);
            // }
        };
        return new Promise((resolve, reject) => {
            loadScript(sdkUrl, true).then(() => {
                resolve({
                    methodMap,
                    available_methods: this.availableMethods,
                    loadWidgetApi: this.loadWidget,
                    loadPlacementApi: this.loadPlacementWidget,
                    magentoApi: {klarnaWaitAuthCallback, klarnaUpdateSession}
                });
            });
        });
    }

    public loadWidget(methodCategory: string) {
        const elId = 'klarna-payments-container';
        const klarnaEl = document.getElementById(elId);
        if (klarnaEl) {
            Klarna.Payments.load({
                container: '#' + elId,
                payment_method_category: methodCategory,
            });
        }
    }

    public loadPlacementWidget() {
        const klarnaStatus = checkout.hasPaymentMethod('klarna_pay_later')
            && store.state.options.klarna_on_site_messages
            && store.state.options.klarna_on_site_messages.on_cart_enabled;
        const klarnaSdk = dataGetter(store.state.options, 'klarna_on_site_messages.libraryurl');
        const klarnaClientId = dataGetter(store.state.options, 'klarna_on_site_messages.clientid');
        if (klarnaSdk && klarnaStatus) {
            loadScript(klarnaSdk, true, {'data-client-id': klarnaClientId}).then((res) => {
                window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
            });
        }
    }

    public pay(amount: number): Promise<PayResult> {
        return new Promise((resolve, reject) => {
            if (checkout.selectedPaymentMethod
                && checkout.selectedPaymentMethod.code
                && !methodMap.hasOwnProperty(checkout.selectedPaymentMethod.code)
            ) {
                reject(i18n.t('Payment method not supported').toString());
            }

            const billingAddress = checkout.paymentBillingAddress;
            const shippingAddress = checkout.shippingAddress;
            const klarnaShipping = {
                given_name: shippingAddress.firstname,
                family_name: shippingAddress.lastname,
                email: checkout.email,
                title: '',
                street_address: shippingAddress.street[0] + ' ' + getStreetNumber(shippingAddress),
                postal_code: shippingAddress.postcode,
                city: shippingAddress.city,
                region: '',
                phone: shippingAddress.telephone,
                country: shippingAddress.country_id,
            };
            const klarnaBilling = {
                given_name: billingAddress.firstname,
                family_name: billingAddress.lastname,
                email: checkout.email,
                title: '',
                street_address: billingAddress.street[0] + ' ' + getStreetNumber(shippingAddress),
                postal_code: billingAddress.postcode,
                city: billingAddress.city,
                region: '',
                phone: billingAddress.telephone,
                country: billingAddress.country_id,
            };

            if (checkout.isStorePickup) {
                if (user.isLoggedin) {
                    klarnaShipping.given_name = billingAddress.firstname;
                    klarnaShipping.family_name = billingAddress.lastname;
                    klarnaShipping.street_address = billingAddress.street[0] + ' ' + getStreetNumber(shippingAddress);
                    klarnaShipping.postal_code = billingAddress.postcode;
                    klarnaShipping.city = billingAddress.city;
                    klarnaShipping.phone = billingAddress.telephone;
                    klarnaShipping.country = billingAddress.country_id;
                }
                const storePickup = storelocator.storeById(storelocator.selectedStoreId) as StoreLocator;
                if (storePickup && storePickup.address) {
                    klarnaShipping.street_address = storePickup.address;
                }
                if (storePickup && storePickup.zipcode) {
                    klarnaShipping.postal_code = storePickup.zipcode;
                }
                if (storePickup && storePickup.city) {
                    klarnaShipping.city = storePickup.city;
                }
                if (storePickup && storePickup.country_id) {
                    klarnaShipping.country = storePickup.country_id;
                }
            }

            // console.log(klarnaShipping, klarnaBilling, amount);

            Klarna.Payments.authorize({
                payment_method_category: methodMap[checkout.selectedPaymentMethod.code],
            }, {
                billing_address: klarnaBilling,
                shipping_address: klarnaShipping,
            }, (res: any) => {
                console.log(res);
                if (true === res.approved) {
                    const authorizationToken = res.authorization_token;
                    this.waitAuth(authorizationToken).then((klarnaGetAuthRes: any) => {
                        const orderId = klarnaGetAuthRes.orderid;
                        if (klarnaGetAuthRes.is_active === '1' || !orderId) { // old flow
                            const payData: PayData = {
                                additional_data: {
                                    method_title: checkout.selectedPaymentMethod.title,
                                    authorization_token: authorizationToken,
                                },
                            };
                            resolve({
                                finalizeData: {payData},
                                needsFinalization: true,
                            });
                        } else { // order is place in authorize webhook
                            resolve({
                                finalizeData: {
                                    payData: {},
                                    checkoutMethod: function(payload: any) {
                                        return new Promise((checkoutResolve, checkoutReject) => {
                                            klarnaUpdateSession(authorizationToken).then(() => {
                                                checkoutResolve(orderId);
                                            }).catch(() => {
                                                checkoutReject(i18n.t('Error during payment authorization').toString() + '.');
                                            });
                                        });
                                    },
                                    doneCallback() {
                                        redirectToLocation('/checkout/onepage/success');
                                    }
                                },
                                needsFinalization: true,
                            });
                        }
                    });
                } else {
                    reject(i18n.t('Error during payment authorization').toString());
                }
            });
        });
    }

    public waitAuth(authorizationToken: string) {
        return new Promise((resolve) => {
            klarnaWaitAuthCallback(authorizationToken).then((res) => {
                resolve(res);
            }).catch((e) => {
                resolve(e.response.data);
            });
        });
    }

    public teardown() {
        return new Promise((resolve, reject) => {
            resolve(true);
        });
    }
}
